import type { Client } from 'graphql-ws';

import { addRecoveryListeners } from './recovery';

export type ClientState = {
	client: Client | null;
	isSocketConnected: () => boolean;
	abortPendingRetryWait: () => boolean;
	observesSocketState: boolean;
	lazyCloseTimeout: number;
};

export type ClientLease = {
	client: Client;
	getSocketConnectionState: () => 'connected' | 'disconnected' | 'unknown';
	release: () => void;
};

export const createClientState = (): ClientState => ({
	client: null,
	isSocketConnected: () => false,
	abortPendingRetryWait: () => false,
	observesSocketState: false,
	lazyCloseTimeout: 0,
});

type Entry = {
	state: ClientState;
	client: Client;
	references: number;
	evictionTimer?: ReturnType<typeof setTimeout>;
	detachRecovery?: () => void;
};

const clients = new Map<string, Entry>();
let recoveryEnabled = false;

function attachRecovery(entry: Entry): void {
	if (recoveryEnabled && entry.references > 0 && !entry.detachRecovery) {
		entry.detachRecovery = addRecoveryListeners({
			terminate: () => entry.client.terminate(),
			isConnected: entry.state.isSocketConnected,
			abortRetryWait: entry.state.abortPendingRetryWait,
		});
	}
}

/** Follow the existing app-mount recovery lifetime without changing its default connection. */
export function setEndpointRecoveryEnabled(enabled: boolean): void {
	recoveryEnabled = enabled;
	for (const entry of clients.values()) {
		if (enabled) {
			attachRecovery(entry);
		} else {
			entry.detachRecovery?.();
			entry.detachRecovery = undefined;
		}
	}
}

export function acquireClient(
	url: string,
	initialize: (state: ClientState, url: string) => Client | null,
	getConnectionState: (state: ClientState) => 'connected' | 'disconnected' | 'unknown',
): ClientLease | null {
	let entry = clients.get(url);
	if (!entry) {
		const state = createClientState();
		const client = initialize(state, url);
		if (!client) {
			return null;
		}
		entry = { state, client, references: 0 };
		clients.set(url, entry);
	}
	const acquired = entry;
	clearTimeout(acquired.evictionTimer);
	acquired.evictionTimer = undefined;
	acquired.references += 1;
	attachRecovery(acquired);
	let released = false;

	return {
		client: acquired.client,
		getSocketConnectionState: () => getConnectionState(acquired.state),
		release: () => {
			if (released) {
				return;
			}
			released = true;
			if (--acquired.references !== 0) {
				return;
			}
			acquired.detachRecovery?.();
			acquired.detachRecovery = undefined;
			const evict = () => {
				// A rapid resubscription can retain the entry during the lazy-close window.
				if (acquired.references !== 0 || clients.get(url) !== acquired) {
					return;
				}
				clients.delete(url);
				try {
					Promise.resolve(acquired.client.dispose()).catch(() => {
						/* disposal errors must not break observer teardown */
					});
				} catch {
					/* disposal errors must not break observer teardown */
				}
			};
			if (acquired.state.lazyCloseTimeout > 0) {
				acquired.evictionTimer = setTimeout(evict, acquired.state.lazyCloseTimeout);
			} else {
				evict();
			}
		},
	};
}
